// ============================================
// FILE: components/quiz/QuizSetup.tsx
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '../common/LoadingSpinner';
import DataService from '../../services/DataService';

interface QuizSetupProps {
  onStartQuiz: (grade: string, reading_mode: string, questions: any[]) => void;
}

export const QuizSetup: React.FC<QuizSetupProps> = ({ onStartQuiz }) => {
  const [inputs, setInputs] = useState<{ grade?: string; reading_mode?: string }>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Get all available grades from student data (filters out empty grades)
  const { data: grades, isLoading: gradesLoading } = useQuery({
    queryKey: ['grades'],
    queryFn: () => DataService.getAvailableGrades(),
  });

  // Get all students data for filtering
  const { data: allStudents, isLoading: studentsLoading } = useQuery({
    queryKey: ['students'],
    queryFn: () => DataService.getStudents(),
  });

  // Get available categories from manhaj data
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => DataService.getAvailableCategories(),
  });

  // Get available reading_mode options based on selected grade
  const availableReadingModes = useMemo(() => {
    if (!inputs.grade || !allStudents) return [];

    // Get all reading modes that have students in this grade
    const studentsInGrade = allStudents.filter(
      s => s.grade && s.grade === inputs.grade && s.reading_mode
    );
    
    const readingModesFromStudents = [...new Set(
      studentsInGrade.map(s => s.reading_mode.toLowerCase())
    )];

    // Filter to only those that have manhaj data
    const validModes = readingModesFromStudents.filter(mode => {
      const manhajRange = DataService.getManhajRange(Number(inputs.grade), mode);
      return manhajRange !== null;
    });

    return validModes;
  }, [inputs.grade, allStudents]);

  // Reset reading_mode when grade changes
  useEffect(() => {
    if (inputs.grade) {
      setInputs(prev => ({ ...prev, reading_mode: '' }));
      setError('');
    }
  }, [inputs.grade]);

  // Get student count for selected grade and reading_mode
  const getStudentCount = () => {
    if (inputs.grade && inputs.reading_mode && allStudents) {
      const students = allStudents.filter(s => 
        s.grade === inputs.grade && 
        s.reading_mode?.toLowerCase() === inputs.reading_mode.toLowerCase()
      );
      return students.length;
    }
    return 0;
  };

  // Get manhaj range for selected grade and reading_mode
  const getManhajInfo = () => {
    if (inputs.grade && inputs.reading_mode) {
      const range = DataService.getManhajRange(Number(inputs.grade), inputs.reading_mode);
      return range;
    }
    return null;
  };

  // Get all students in selected grade and reading_mode
  const getStudentsList = () => {
    if (inputs.grade && inputs.reading_mode && allStudents) {
      return allStudents.filter(s => 
        s.grade === inputs.grade && 
        s.reading_mode?.toLowerCase() === inputs.reading_mode.toLowerCase()
      );
    }
    return [];
  };

  // Function to get 2 random questions from the syllabus
  const getRandomQuestions = () => {
    if (!inputs.grade || !inputs.reading_mode) return [];

    const manhajRange = DataService.getManhajRange(Number(inputs.grade), inputs.reading_mode);
    if (!manhajRange) return [];

    // Get all questions in the range
    const allQuestions = DataService.getQuestionsByVidRange(manhajRange.from, manhajRange.to);
    
    if (allQuestions.length === 0) {
      setError('No questions found in this syllabus range');
      return [];
    }

    // Shuffle and get 2 random questions
    const shuffled = [...allQuestions].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, 2);
    
    // If we have less than 2 questions, return all available
    if (selected.length < 2) {
      setError(`Only ${selected.length} question(s) available in this syllabus`);
      return selected;
    }

    return selected;
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.target;
    setInputs(prev => ({ ...prev, [id]: value }));
    setError('');
  };

  const handleStart = () => {
    const { grade, reading_mode } = inputs;
    
    if (!grade || grade === 'choose') {
      setError('Please select a grade');
      return;
    }
    
    if (!reading_mode || reading_mode === 'choose') {
      setError('Please select a reading mode');
      return;
    }

    // Validate that there are students with this combination
    const studentCount = getStudentCount();
    if (studentCount === 0) {
      setError(`No students found for grade ${grade} with ${reading_mode}`);
      return;
    }

    // Validate that manhaj exists for this combination
    const manhajRange = DataService.getManhajRange(Number(grade), reading_mode);
    if (!manhajRange) {
      setError(`No syllabus found for grade ${grade} with ${reading_mode}`);
      return;
    }

    // Get 2 random questions
    const selectedQuestions = getRandomQuestions();
    
    if (selectedQuestions.length === 0) {
      setError('No questions available for this syllabus');
      return;
    }

    if (selectedQuestions.length < 2) {
      setError(`Only ${selectedQuestions.length} question(s) available. Need at least 2 to start quiz.`);
      return;
    }

    setLoading(true);
    setTimeout(() => {
      // Pass the selected questions to the quiz
      onStartQuiz(grade, reading_mode, selectedQuestions);
      setLoading(false);
    }, 500);
  };

  if (gradesLoading || studentsLoading) {
    return (
      <div className="bg-gradient-to-br from-purple-600 to-purple-900 rounded-xl shadow-2xl max-w-md mx-auto p-6">
        <LoadingSpinner />
      </div>
    );
  }

  const gradeOptions = (grades || []).map(grade => ({
    id: Number(grade),
    body: grade
  }));

  const readingModeOptions = availableReadingModes.map(readingMode => ({
    id: readingMode,
    body: readingMode.charAt(0).toUpperCase() + readingMode.slice(1)
  }));

  const studentCount = getStudentCount();
  const manhajInfo = getManhajInfo();
  const studentsList = getStudentsList();

  // Get preview of available questions
  const getQuestionPreview = () => {
    if (!inputs.grade || !inputs.reading_mode) return null;
    
    const manhajRange = DataService.getManhajRange(Number(inputs.grade), inputs.reading_mode);
    if (!manhajRange) return null;
    
    const allQuestions = DataService.getQuestionsByVidRange(manhajRange.from, manhajRange.to);
    return {
      total: allQuestions.length,
      random: allQuestions.slice(0, 2).map(q => q.id)
    };
  };

  const questionPreview = getQuestionPreview();

  return (
    <div className="bg-gradient-to-br from-purple-600 to-purple-900 rounded-xl shadow-2xl max-w-md mx-auto p-6">
      <h2 className="text-white text-2xl font-bold text-center mb-6">
        📖 Quran Quiz
      </h2>
      
      <div className="mb-4">
        <label htmlFor="grade" className="block text-gray-100 text-sm font-bold mb-2">
          Select Grade
        </label>
        <select
          id="grade"
          className="w-full p-2 border rounded text-black bg-white"
          value={inputs.grade || 'choose'}
          onChange={handleChange}
        >
          <option value="choose">Choose Grade</option>
          {gradeOptions.map((option) => (
            <option key={option.id} value={option.id}>
              Grade {option.body}
            </option>
          ))}
        </select>
        {inputs.grade && (
          <p className="text-gray-300 text-xs mt-1">
            {allStudents?.filter(s => s.grade === inputs.grade).length || 0} students in this grade
          </p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="reading_mode" className="block text-gray-100 text-sm font-bold mb-2">
          Select Reading Mode
        </label>
        <select
          id="reading_mode"
          className="w-full p-2 border rounded text-black bg-white"
          value={inputs.reading_mode || 'choose'}
          onChange={handleChange}
          disabled={!inputs.grade || availableReadingModes.length === 0}
        >
          <option value="choose">
            {!inputs.grade 
              ? 'Select grade first' 
              : availableReadingModes.length === 0 
                ? 'No reading modes available' 
                : 'Choose Reading Mode'}
          </option>
          {readingModeOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.body}
            </option>
          ))}
        </select>
        
        {inputs.grade && inputs.reading_mode && (
          <div className="mt-2 space-y-1">
            <p className="text-gray-300 text-xs">
              👥 {studentCount} student{studentCount !== 1 ? 's' : ''} found
            </p>
            {studentCount > 0 && studentCount <= 5 && (
              <div className="text-gray-300 text-xs">
                <p className="font-semibold">Students:</p>
                <ul className="list-disc list-inside ml-2">
                  {studentsList.map(student => (
                    <li key={student.nid}>
                      {student.name_en} {student.cls && `(${student.cls})`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {manhajInfo && (
              <p className="text-gray-300 text-xs bg-purple-500/20 p-1 rounded">
                📚 Syllabus: {manhajInfo.category} (Verses {manhajInfo.from} - {manhajInfo.to})
              </p>
            )}
            {questionPreview && (
              <p className="text-gray-300 text-xs bg-green-500/20 p-1 rounded">
                📝 {questionPreview.total} questions available in this range
                {questionPreview.total >= 2 && ' ✅ 2 random questions will be selected'}
                {questionPreview.total < 2 && ` ⚠️ Only ${questionPreview.total} question(s) available`}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Show available categories info */}
      {categories && categories.length > 0 && inputs.grade && (
        <div className="mb-4 p-2 bg-purple-500/20 rounded-lg">
          <p className="text-gray-300 text-xs">
            Available reading modes for this grade: {availableReadingModes.join(', ')}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500/20 border border-red-400 text-red-100 px-4 py-2 rounded mb-4 text-center">
          {error}
        </div>
      )}

      <button
        onClick={handleStart}
        disabled={loading || !inputs.grade || !inputs.reading_mode || studentCount === 0}
        className={`
          w-full py-3 px-4 rounded-lg font-bold text-white
          transition-all duration-200 transform
          ${loading || !inputs.grade || !inputs.reading_mode || studentCount === 0
            ? 'bg-gray-500 cursor-not-allowed opacity-50'
            : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105'
          }
        `}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <LoadingSpinner size="small" />
            <span className="ml-2">Loading...</span>
          </span>
        ) : (
          'Start Quiz 🚀'
        )}
      </button>
    </div>
  );
};